import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import SolidExclamationTriangleIcon from 'LuxKit/Icons/solid/SolidExclamationTriangleIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { getThemeClassName } from 'lib/theme/themeUtils'
import ModalContext from 'contexts/ModalContext'
import LuxPlusManageAccountModal from '../LuxPlusManageAccount/LuxPlusManageAccountModal'
import LuxPlusMemberLandingLayout from './LuxPlusMemberLandingLayout'
import { useAppSelector } from 'hooks/reduxHooks'

const FailedRenewalCard = styled(Pane)`
  max-width: ${rem(400)};
`

const FailedRenewalCardBody = styled.div`
  padding: ${rem(20)} ${rem(28)};
`

function LuxPlusFailedPaymentLandingPageContent() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const subscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)

  const handleManageMembership = useCallback(() => {
    showModal(<LuxPlusManageAccountModal />)
  }, [showModal])

  const failedRenewalHeading = intl.formatMessage({
    id: 'bNQKx9FyNOdH_a_GqJaU',
    defaultMessage: 'Membership failed to renew',
    description: 'Heading for the failed payment renewal card on LuxPlus landing page',
  })

  const updatePaymentLabel = intl.formatMessage({
    id: 'ia8VrZm1OOdJyzQuPjd2',
    defaultMessage: 'Update payment details',
    description: 'Button to update payment details after a failed LuxPlus membership renewal',
  })

  const failedPaymentDateDisplay = subscriptionItem?.finalChargeAttemptDate
    ? <FormatDateTime value={subscriptionItem.finalChargeAttemptDate} dateStyle="medium" />
    : intl.formatMessage({
        id: 'L-IpsnRuqnYoT6bc0P6L',
        defaultMessage: 'the due date',
        description: 'Fallback text when the final payment attempt date is not available',
      })

  const failedRenewalCard = (
    <FailedRenewalCard className={getThemeClassName({ mobile: 'default' })}>
      <FailedRenewalCardBody>
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <SolidExclamationTriangleIcon colour="critical" />
            <Heading variant="heading6" colour="critical">
              {failedRenewalHeading}
            </Heading>
          </Group>
          <BodyText variant="medium" colour="neutral-two">
            <FormattedMessage
              id="vUBXr-khuB9iuFvRiSk9"
              defaultMessage="There was a problem with your payment, so your benefits are on hold. Please update your payment details by {date} to keep your membership active."
              description="Message explaining that the membership renewal payment failed and benefits are on hold"
              values={{
                date: failedPaymentDateDisplay,
              }}
            />
          </BodyText>
          <TextButton kind="primary" fit="full-width" onClick={handleManageMembership}>
            {updatePaymentLabel}
          </TextButton>
        </VerticalSpacer>
      </FailedRenewalCardBody>
    </FailedRenewalCard>
  )

  return (
    <LuxPlusMemberLandingLayout>
      {failedRenewalCard}
    </LuxPlusMemberLandingLayout>
  )
}

export default LuxPlusFailedPaymentLandingPageContent
